import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa6';
import { NS } from '../../U3204';
import {
  u3204ReadThinkingPanelCollapsed,
  u3204WriteThinkingPanelCollapsed,
} from '../../utils/u3204ThinkingPanelState';

interface U3204ThinkingPanelProps {
  conversationId: string | null;
  thinkingContent: string;
  isInProgress: boolean;
}

const U3204ThinkingPanel: React.FC<U3204ThinkingPanelProps> = ({
  conversationId,
  thinkingContent,
  isInProgress,
}) => {
  const { t } = useTranslation(NS);
  const [collapsed, setCollapsed] = useState(() => u3204ReadThinkingPanelCollapsed(conversationId));

  useEffect(() => {
    setCollapsed(u3204ReadThinkingPanelCollapsed(conversationId));
  }, [conversationId]);

  const handleToggle = useCallback(() => {
    const next = !collapsed;
    setCollapsed(next);
    u3204WriteThinkingPanelCollapsed(conversationId, next);
  }, [collapsed, conversationId]);

  const headerLabel = isInProgress ? t('thinking.inProgress', { ns: NS }) : t('thinking.title', { ns: NS });
  const toggleLabel = collapsed ? t('thinking.expand', { ns: NS }) : t('thinking.collapse', { ns: NS });

  return (
    <div className="mb-2 w-full rounded-xl border border-neutral-200 bg-neutral-100/80 text-sm text-neutral-600 shadow-xs">
      <button
        type="button"
        onClick={handleToggle}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium text-foreground/80 hover:bg-muted"
        aria-expanded={!collapsed}
        aria-label={toggleLabel}
      >
        <span className="flex items-center gap-2">
          {isInProgress ? (
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
          ) : null}
          <span>{headerLabel}</span>
        </span>
        {collapsed ? (
          <FaChevronDown className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
        ) : (
          <FaChevronUp className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
        )}
      </button>
      {!collapsed ? (
        <div
          className="max-h-64 overflow-y-auto border-t border-border/40 px-3 py-2 italic whitespace-pre-wrap"
          aria-live="polite"
        >
          {thinkingContent}
          {isInProgress ? (
            <span className="ml-0.5 inline-block h-3 w-0.5 animate-pulse bg-current align-middle" />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default U3204ThinkingPanel;
